/**
 * AdminCommissions — monthly commission analytics for the admin.
 *
 * Shows a table with one row per BDC agent: showed-up count, commission
 * ($50 × count), ACH verification status, YTD. Footer row totals platform
 * gross ($300/appt), commissions paid, and net for the month + YTD.
 *
 * BDC agents do NOT see this page (route is admin-only). They only see
 * their own earnings via /bdc/dashboard/earnings which never surfaces the
 * $300 dealer charge.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, CheckCircle2, Clock, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";

const cents = (n) => formatUSD((n || 0) / 100);

const monthOptions = () => {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString(undefined, { month: "long", year: "numeric" }),
    });
  }
  return out;
};

const AdminCommissions = () => {
  const options = useMemo(monthOptions, []);
  const [month, setMonth] = useState(options[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/commissions", { params: { month } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8" data-testid="admin-commissions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Analytics</p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-slate-900">BDC commissions</h1>
          <p className="mt-1 text-sm text-slate-500">$50 per showed-up appointment · $300 dealer charge · monthly rollup.</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="month-picker" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Month</label>
          <select
            id="month-picker"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium"
            data-testid="admin-commissions-month-picker"
          >
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Totals summary */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Showed up (mo)"
          value={loading ? "…" : String(data?.totals?.month_showed_up ?? 0)}
          icon={<TrendingUp size={14} className="text-emerald-600" />}
          testId="totals-showed-up-month"
        />
        <SummaryCard
          label="Gross (mo)"
          value={loading ? "…" : cents(data?.totals?.month_gross_cents)}
          icon={<Banknote size={14} className="text-slate-500" />}
          testId="totals-gross-month"
        />
        <SummaryCard
          label="Commissions (mo)"
          value={loading ? "…" : cents(data?.totals?.month_commission_cents)}
          icon={<Banknote size={14} className="text-amber-600" />}
          testId="totals-commissions-month"
        />
        <SummaryCard
          label="Net (mo)"
          value={loading ? "…" : cents(data?.totals?.month_net_cents)}
          icon={<Banknote size={14} className="text-brand" />}
          testId="totals-net-month"
          highlight
        />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-commissions-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3">Agent</th>
              <th className="px-4 py-3 text-right">Showed up (mo)</th>
              <th className="px-4 py-3 text-right">Commission (mo)</th>
              <th className="px-4 py-3">Payout status</th>
              <th className="px-4 py-3 text-right">Showed up (YTD)</th>
              <th className="px-4 py-3 text-right">Commission (YTD)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">Loading…</td></tr>
            ) : (
              data?.rows?.map((r) => (
                <tr key={r.agent.id} className="hover:bg-slate-50/70" data-testid={`admin-commissions-row-${r.agent.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">{r.agent.full_name}</p>
                    <p className="text-[11px] text-slate-500">{r.agent.email}</p>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{r.month_showed_up}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{cents(r.month_commission_cents)}</td>
                  <td className="px-4 py-3">
                    <PayoutPill row={r} />
                  </td>
                  <td className="px-4 py-3 text-right">{r.ytd_showed_up}</td>
                  <td className="px-4 py-3 text-right">{cents(r.ytd_commission_cents)}</td>
                </tr>
              ))
            )}
          </tbody>
          {!loading && data?.totals && (
            <tfoot>
              <tr className="bg-slate-900 text-white font-semibold text-sm" data-testid="admin-commissions-totals-row">
                <td className="px-4 py-3">Totals</td>
                <td className="px-4 py-3 text-right">{data.totals.month_showed_up}</td>
                <td className="px-4 py-3 text-right">{cents(data.totals.month_commission_cents)}</td>
                <td className="px-4 py-3 text-[11px] text-white/70">Platform net: <span className="text-white">{cents(data.totals.month_net_cents)}</span></td>
                <td className="px-4 py-3 text-right">{data.totals.ytd_showed_up}</td>
                <td className="px-4 py-3 text-right">{cents(data.totals.ytd_commission_cents)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="mt-4 text-[11px] text-slate-500">
        Platform gross is $300/appt (Phase 5 dealer billing). Commission ($50/appt) is deducted from gross to compute net.
        Only ACH-verified agents will be included in the next payout run.
      </p>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, testId, highlight }) => (
  <div
    className={`rounded-2xl border p-4 ${highlight ? "bg-brand text-white border-brand" : "bg-white border-slate-200"}`}
    data-testid={testId}
  >
    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${highlight ? "text-white/80" : "text-slate-500"}`}>
      {icon} <span>{label}</span>
    </div>
    <p className={`mt-1.5 font-display text-2xl font-semibold ${highlight ? "text-white" : "text-slate-900"}`}>{value}</p>
  </div>
);

const PayoutPill = ({ row }) => {
  if (!row.has_ach_form) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-rose-100 text-rose-800 px-2 py-1">
        <X size={11} /> No ACH form
      </span>
    );
  }
  if (row.ach_verified) {
    return (
      <Link to={`/admin/bdc-agents/${row.agent.id}/payout`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-800 px-2 py-1 hover:bg-emerald-200">
        <CheckCircle2 size={11} /> Verified
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 px-2 py-1">
      <Clock size={11} /> Pending
    </span>
  );
};

export default AdminCommissions;
